<template>
  <container>
    <div class="pg-title-row">
      <div>
        <h1 class="pg-title">Scheduled Runs</h1>
        <p class="pg-title-sub">Search upcoming runs by trainer, date and run details</p>
      </div>
      <span class="sr-total-badge">{{ totalElements }} scheduled</span>
    </div>

    <div class="sr-filter-card">
      <div class="sr-card-header">
        <span class="sr-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"/>
            <path d="M21 21l-4.35-4.35"/>
          </svg>
        </span>
        <div>
          <h2 class="sr-card-title">Run Filters</h2>
          <p class="sr-card-subtitle">Narrow the scheduled queue without leaving the list</p>
        </div>
      </div>

      <div class="sr-controls">
        <div class="sr-control-group sr-control-group--query">
          <label class="sr-control-label">Search Run</label>
          <input type="text" v-model="filters.query" placeholder="Run name or ID" @keyup.enter="performSearch" class="sr-control-input">
        </div>

        <div class="sr-control-group">
          <label class="sr-control-label">Trainer</label>
          <span class="sr-select-wrap">
            <select v-model="filters.trainerId" @change="performSearch" class="sr-control-select">
                <option value="">All Trainers</option>
                <option v-for="t in allTrainers" :key="t.id" :value="t.id">
                    {{ t.firstName }} {{ t.lastName }}
                </option>
            </select>
          </span>
        </div>

        <div class="sr-control-group">
          <label class="sr-control-label">Date</label>
          <input type="date" v-model="filters.date" @change="performSearch" class="sr-control-input">
        </div>

        <button @click="performSearch" class="sr-search-btn">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"/>
            <path d="M21 21l-4.35-4.35"/>
          </svg>
          Search
        </button>
      </div>
    </div>

    <div class="sr-list-card">
      <div class="sr-card-header sr-card-header--list">
        <span class="sr-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2"/>
            <path d="M16 2v4M8 2v4M3 10h18"/>
            <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01"/>
          </svg>
        </span>
        <div>
          <h2 class="sr-card-title">Upcoming Runs</h2>
          <p class="sr-card-subtitle">Review future runs and resume live run operations</p>
        </div>
        <span class="sr-mode-badge">{{ filters.pageSize }} per page</span>
      </div>

      <div v-if="scheduledRunsLoading" class="sr-table-skeleton">
        <div class="sr-skeleton-header">
          <div v-for="n in 8" :key="'sr-head-' + n" class="sr-sk-cell sk-shimmer"></div>
        </div>
        <div v-for="row in 7" :key="'sr-row-' + row" class="sr-skeleton-row">
          <div v-for="col in 8" :key="'sr-cell-' + row + '-' + col" class="sr-sk-cell sk-shimmer"></div>
        </div>
      </div>

      <race-table-list
        v-else
        :races="racesScheduled"
        message-empty-list="There are no scheduled runs"
        table-title="Scheduled Runs"
        table-subtitle="Review upcoming runs and open live operation details"
        empty-title="No scheduled runs found"
        date-field="date"
        type="resume">
      </race-table-list>
    </div>

    <div v-if="!scheduledRunsLoading && totalElements > 0" class="sr-pagination-container">
      <paginate
        v-model="filters.pageNo"
        :page-count="Math.ceil(totalElements / filters.pageSize)"
        :page-range="7"
        :margin-pages="2"
        :click-handler="changePageHandler"
        :prev-text="'Prev'"
        :next-text="'Next'"
        :container-class="'pagination custom-pagination'"
        :page-class="'page-item'">
      </paginate>
    </div>
  </container>
</template>

<script>
import Container from '@/components/Container'
import RaceTableList from '@/components/Race/RaceTableList'
import { mapActions, mapGetters } from 'vuex'
import racesByTrainerService from '@/services/racesByTrainer'
import Paginate from 'vuejs-paginate'

export default {
  name: 'scheduled-runs',
  data () {
    return {
      racesScheduled: [],
      totalElements: 0,
      filters: {
        query: '',
        trainerId: '',
        date: '',
        pageNo: 1,
        pageSize: 25
      },
      scheduledRunsLoading: false
    }
  },
  computed: {
    ...mapGetters({
      allTrainers: 'trainers/allTrainers'
    })
  },
  methods: {
    initialize () {
      this.fetchAllTrainersList()
      this.fetchRuns()
    },
    fetchRuns () {
      this.scheduledRunsLoading = true
      this.addTaskToQueue('fetchScheduledRuns')

      var params = {
        pageNo: this.filters.pageNo,
        pageSize: this.filters.pageSize,
        query: this.filters.query,
        trainerId: this.filters.trainerId,
        date: this.filters.date,
        filter: 'future'
      }

      racesByTrainerService.getAllRuns(params)
        .then(function (response) {
          this.totalElements = response.body.totalElements || 0
          // For scheduled runs startDate is null; use date as the start time source
          // so RaceTableList can format h:mm A correctly from race.date
          var runs = response.body.content || []
          this.racesScheduled = runs.map(function (run) {
            if (!run.startDate) {
              return Object.assign({}, run, { startDate: run.date })
            }
            return run
          })
        }.bind(this))
        .catch(function (err) { console.error(err) })
        .finally(function () {
          this.scheduledRunsLoading = false
          this.removeTaskFromQueue('fetchScheduledRuns')
        }.bind(this))
    },
    performSearch () {
      this.filters.pageNo = 1
      this.fetchRuns()
    },
    changePageHandler (pageNum) {
      this.filters.pageNo = pageNum
      this.fetchRuns()
    },
    ...mapActions({
      fetchAllTrainersList: 'trainers/fetchAllTrainersList',
      addTaskToQueue: 'LoadingQueue/addTaskToQueue',
      removeTaskFromQueue: 'LoadingQueue/removeTaskFromQueue'
    })
  },
  created () {
    this.initialize()
  },
  components: {
    Container,
    RaceTableList,
    Paginate
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;900&display=swap');
</style>

<style scoped>
.pg-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f5;
  margin-bottom: 1.5rem;
  gap: 1rem;
}

.pg-title {
  font-family: 'Inter', sans-serif;
  font-size: 34px;
  font-weight: 900;
  letter-spacing: -0.04em;
  color: #1a1a2e;
  line-height: 1.1;
  margin: 0;
}

.pg-title-sub {
  font-size: 13px;
  color: #8a8a9a;
  font-weight: 400;
  margin: 5px 0 0;
}

.sr-total-badge,
.sr-mode-badge {
  min-height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #f0effe;
  color: #413b94;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.sr-filter-card,
.sr-list-card {
  border: 1px solid #e8e8ed;
  border-radius: 14px;
  padding: 1.5rem 1.75rem;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  background: #fff;
}

.sr-list-card {
  margin-top: 1rem;
}

.sr-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 1.2rem;
}

.sr-card-header--list {
  justify-content: space-between;
}

.sr-card-header--list .sr-icon {
  flex: 0 0 36px;
}

.sr-card-header--list > div {
  flex: 1;
  min-width: 0;
}

.sr-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f0effe;
  color: #5b4fcf;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.sr-card-title {
  color: #1a1a2e;
  font-size: 16px;
  font-weight: 700;
  margin: 0;
}

.sr-card-subtitle {
  color: #8a8a9a;
  font-size: 12px;
  margin: 4px 0 0;
}

.sr-controls {
  display: grid;
  grid-template-columns: minmax(220px, 1.5fr) minmax(190px, 1fr) minmax(160px, 0.8fr) auto;
  gap: 14px;
  align-items: end;
}

.sr-control-group {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.sr-control-label {
  font-weight: 600;
  font-size: 12px;
  margin-bottom: 6px;
  color: #1a1a2e;
}

.sr-control-input,
.sr-control-select {
  width: 100%;
  height: 36px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #e8e8ed;
  font-size: 13px;
  color: #1a1a2e;
  background: #fafafa;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
  box-sizing: border-box;
}

.sr-control-select {
  appearance: none;
  padding-right: 34px;
}

.sr-select-wrap {
  position: relative;
  display: block;
}

.sr-select-wrap::after {
  content: "";
  position: absolute;
  right: 12px;
  top: 50%;
  width: 10px;
  height: 10px;
  transform: translateY(-50%);
  pointer-events: none;
  background-image: url("data:image/svg+xml,%3Csvg width='10' height='10' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5 7.5L10 12.5L15 7.5' stroke='%238a8a9a' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
}

.sr-control-input:focus,
.sr-control-select:focus {
  outline: none;
  border-color: #5b4fcf;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(91, 79, 207, 0.1);
}

.sr-search-btn {
  height: 36px;
  padding: 0 18px;
  border-radius: 8px;
  border: none;
  background: #5b4fcf;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: background 0.2s ease, transform 0.2s ease;
  white-space: nowrap;
}

.sr-search-btn:hover {
  background: #4338ca;
}

.sr-table-skeleton {
  border: 1px solid #f0f0f5;
  border-radius: 10px;
  overflow: hidden;
}

.sr-skeleton-header,
.sr-skeleton-row {
  display: grid;
  grid-template-columns: 80px 100px 100px 110px minmax(180px, 1fr) 110px 120px 100px;
  gap: 12px;
  align-items: center;
  padding: 11px 14px;
}

.sr-skeleton-header {
  background: #fafafa;
}

.sr-skeleton-row + .sr-skeleton-row {
  border-top: 1px solid #f0f0f5;
}

.sr-sk-cell {
  height: 14px;
  border-radius: 6px;
}

@keyframes sk-shimmer {
  0% { background-position: -600px 0; }
  100% { background-position: 600px 0; }
}

.sk-shimmer {
  background: linear-gradient(90deg,#f0f0f5 25%,#e4e4ef 50%,#f0f0f5 75%);
  background-size: 600px 100%;
  animation: sk-shimmer 1.4s ease-in-out infinite;
  border-radius: 6px;
}

.sr-pagination-container {
  margin-top: 1rem;
  display: flex;
  justify-content: center;
}

::v-deep .raceTable {
  border-radius: 10px;
  overflow: hidden;
}

::v-deep .raceTable .v-table-title-row {
  background: #1a1a2e !important;
}

::v-deep .raceTable .v-table-title-cell {
  color: #fff !important;
  font-size: 12px !important;
  font-weight: 600;
}

::v-deep .raceTable .v-table-body-cell {
  color: #1a1a2e;
  font-size: 13px;
}

::v-deep .rtl-table-link {
  color: #5b4fcf;
  font-weight: 700;
}

::v-deep .btn.btn--primary {
  background: #fff;
  color: #5b4fcf;
  border: 1.5px solid #5b4fcf;
  height: 33px;
  border-radius: 7px;
  padding: 0 14px;
  font-size: 13px;
  font-weight: 600;
  margin-top: 1rem;
}

::v-deep .pagination {
  display: flex;
  list-style: none;
  padding: 0;
  margin: 0;
  gap: 6px;
}

::v-deep .pagination li {
  display: inline-block;
}

::v-deep .pagination li a {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 34px;
  height: 34px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid #e8e8ed;
  background: #fff !important;
  color: #1a1a2e !important;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

::v-deep .pagination li a:hover {
  background: #f0effe !important;
  border-color: #5b4fcf;
  color: #5b4fcf !important;
}

::v-deep .pagination .active,
::v-deep .pagination li.active,
::v-deep .pagination li.active a,
::v-deep .pagination li.active span {
  background: #5b4fcf !important;
  background-color: #5b4fcf !important;
  color: #fff !important;
  border-color: #5b4fcf !important;
}

::v-deep li.page-item.active a,
::v-deep .pagination li.page-item.active a {
  color: #fff !important;
}

::v-deep .pagination li.disabled a {
  color: #aaa;
  cursor: not-allowed;
  background: #f5f5f5;
  border-color: #eee;
}

@media (max-width: 980px) {
  .sr-controls {
    grid-template-columns: 1fr 1fr;
  }

  .sr-search-btn {
    width: 100%;
  }

  .sr-skeleton-header,
  .sr-skeleton-row {
    grid-template-columns: repeat(4, minmax(80px, 1fr));
  }
}

@media (max-width: 640px) {
  .pg-title-row,
  .sr-card-header--list {
    align-items: flex-start;
    flex-direction: column;
  }

  .pg-title {
    font-size: 30px;
  }

  .sr-filter-card,
  .sr-list-card {
    padding: 1.25rem;
  }

  .sr-controls {
    grid-template-columns: 1fr;
  }

  .sr-skeleton-header,
  .sr-skeleton-row {
    grid-template-columns: repeat(2, minmax(90px, 1fr));
  }
}
</style>



// WEBPACK FOOTER //
// ScheduledRuns.vue?1418ad0b